import { useEffect, useState, useRef, useCallback, useMemo, useId, type ReactNode } from "react";
import NameFilterBar from '../../components/NameFilterBar';
import { nachNamenFiltern } from '../../utilities/namensfilter';

import { userAPI } from "../../UserAPI";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { KnowledgeBase, MarketplaceOffer } from "@the-real-insight/tri-model";
import { useModalContext } from "../../components/ModalProvider";
import { useTranslation } from "react-i18next";
import 'react-medium-image-zoom/dist/styles.css';
import PlanPanel from '../PlanPanel';
import MarketingDetails from '../MarketingDetails';
import { useTestDialog } from "../hooks/useTestDialog";
import SampleRequests from "../SampleRequests";
import { OfferStatus } from "@the-real-insight/tri-model";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useUrlPageState } from "../../utilities/dataTablePagination";

import truncate from "lodash.truncate";
import OfferStatusManagement, { InternallyAvailableCheckbox } from '../OfferStatusManagement';
import { withMarketplaceMenuItems } from '../marketplacePublishing';
import { FluentIconSelector } from "../../components/FluentIconSelector";
import { ColorSelector } from "../../components/ColorSelector";
import { ToolUsageList } from "../components/ToolUsageList";
import FileManagement from "./FileManagement";
import DocumentCardsPanel from "./DocumentCardsPanel";
import { getKnowledgeBases } from "../../KnowledgeBasesAPI";
import { OfferRowMenu, YesNoBadge, rowMenuColumnMeta } from "../components/offerTable";
import { MarketplaceOfferNameCell } from "../components/MarketplaceOfferNameCell";

import type { Column, ColumnDef } from "@tanstack/react-table";
import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Loader2,
    Plus,
    Rocket,
    Trash2,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

// Add global style override
const monacoStyle = `
  .monaco-editor * {
    font-family: monospace !important;
    font-weight: bold !important;
    font-size: 12px !important;
  }
`;

/** Klickbarer Spaltenkopf — die `DataTable` bringt keine Sortierköpfe mit. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                : sorted === 'desc'
                    ? <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden />}
        </Button>
    );
}

/** Spaltenkopf ohne Sortierung — gleiche Schriftstärke/Farbe wie die sortierbaren Köpfe. */
const plainHeader = (text: string) => () => <span className="font-bold text-foreground">{text}</span>;

export default function RAGManagement() {
    const { t, i18n } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog } = useModalContext() as any;
    const { TestDialog, openTestDialog, closeTestDialog: _closeTestDialog } = useTestDialog();
    const [searchParams, setSearchParams] = useSearchParams();
    const knowledgeBasePagination = useUrlPageState('knowledgeBasePage');
    const kbLinksPagination = useUrlPageState('kbLinksPage');
    const _navigate = useNavigate();
    const fieldId = useId();
    const [knowledgeBases, setKnowledgeBases] = useState<any[]>([]);
    /* Suchfeld über der Tabelle: Teilzeichenkette im Namen, siehe utilities/namensfilter.ts. */
    const [knowledgeBaseFilter, setKnowledgeBaseFilter] = useState('');
    const gefilterteKnowledgeBases = useMemo(
        () => nachNamenFiltern(knowledgeBases, knowledgeBaseFilter),
        [knowledgeBases, knowledgeBaseFilter]
    );
    const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<any>();
    const initialKey = searchParams.get('key') || 'documents'; // Default to Documents tab
    const [selectedKey, setSelectedKey] = useState<any>(initialKey);
    const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
    const [linkUrl, setLinkUrl] = useState<string>('');
    const linkInputRef = useRef<HTMLInputElement | null>(null);
    // Starts true: the first frame shows the table skeleton, not an empty-state flash.
    const [loadingKnowledgeBases, setLoadingKnowledgeBases] = useState(true);
    const _isUpdating = useRef(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [localName, setLocalName] = useState('');
    const [localDescription, setLocalDescription] = useState('');
    // Description optimization: cache of the pre-optimization value so the user
    // can revert as long as the KB hasn't been saved yet.
    const [previousDescription, setPreviousDescription] = useState<string | null>(null);
    const [optimizingDescription, setOptimizingDescription] = useState(false);

    // Keep selectedKnowledgeBase in sync with the 'knowledgeBase' query parameter
    useEffect(() => {
        const knowledgeBaseParam = searchParams.get("knowledgeBase") || '';
        if (knowledgeBaseParam !== selectedKnowledgeBase?._id) {
            loadKnowledgeBaseFromURL();
        }
    }, [searchParams]);

    const deleteKnowledgeBase = (knowledgeBase: any) => {
        try {
            showDeleteDialog(`${t('ui.deleteKnowledgeBase')} ${knowledgeBase.name}`,
                knowledgeBase.name,
                async () => {
                    await userAPI.deleteMarketplaceOffer(authentication.customer, knowledgeBase);
                    setKnowledgeBases(knowledgeBases.filter((kb: any) => kb._id !== knowledgeBase._id));
                });
        } catch (error) {
            pushError(error);
        }
    };

    const knowledgeBaseColumns: ColumnDef<any>[] = [{
        id: "name",
        size: 340,
        accessorFn: (row: any) => row.name,
        header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
        cell: ({ row }) => <MarketplaceOfferNameCell offer={row.original} />,
    }, {
        id: "files",
        size: 100,
        accessorFn: (row: any) => row.managedFiles ? row.managedFiles.length : 0,
        header: ({ column }) => <SortableHeader column={column}>Dateien</SortableHeader>,
        cell: ({ row }) => <div className="text-muted-foreground">{row.original.managedFiles ? row.original.managedFiles.length : '-'}</div>,
    }, {
        id: 'published',
        size: 160,
        accessorFn: (knowledgeBase: any) => knowledgeBase.published,
        header: ({ column }) => <SortableHeader column={column}>Intern verfügbar</SortableHeader>,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.published)} />,
    }, {
        id: 'offered',
        size: 160,
        accessorFn: (knowledgeBase: any) => knowledgeBase.offered,
        header: ({ column }) => <SortableHeader column={column}>Im Marktplatz</SortableHeader>,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.offered)} />,
    }, {
        id: "description",
        size: 400,
        header: plainHeader('Beschreibung'),
        cell: ({ row }) => <div className="line-clamp-2 min-w-0 break-words text-muted-foreground">{truncate(row.original.synopsis, { length: 200, separator: /,? +/ })}</div>
    }, {
        id: 'rowMenu',
        size: 56,
        header: () => <span className="sr-only">{t('ui.actions', 'Aktionen')}</span>,
        meta: rowMenuColumnMeta,
        cell: ({ row }) => (
            <OfferRowMenu
                onEdit={() => updateSelectedKnowledgeBase(row.original)}
                onTest={() => openTestDialog(row.original)}
                onDelete={() => deleteKnowledgeBase(row.original)}
            />
        ),
    }
    ];
    // linkColumns defined later (after updateSelectedKnowledgeBase)




    const loadKnowledgeBases = async () => {
        try {
            setLoadingKnowledgeBases(true);

            const knowledgeBases = await getKnowledgeBases(authentication.customer);

            setKnowledgeBases(knowledgeBases);
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingKnowledgeBases(false);
        }
    }

    // Helper to update knowledge base state directly
    const setSelectedKnowledgeBaseSafely = useCallback((nextKb: any) => {
        setSelectedKnowledgeBase((prev: any) => {
            const prevId = prev?._id || '';
            const nextId = nextKb?._id || '';
            const prevLinks = Array.isArray(prev?.links) ? prev.links : [];
            const nextLinks = Array.isArray(nextKb?.links) ? nextKb.links : [];
            const prevImages = Array.isArray(prev?.images) ? prev.images : [];
            const nextImages = Array.isArray(nextKb?.images) ? nextKb.images : [];

            const prevManagedFiles = Array.isArray(prev?.managedFiles) ? prev.managedFiles : [];
            const nextManagedFiles = Array.isArray(nextKb?.managedFiles) ? nextKb.managedFiles : [];
            const prevSampleRequests = Array.isArray(prev?.sampleRequests) ? prev.sampleRequests : [];
            const nextSampleRequests = Array.isArray(nextKb?.sampleRequests) ? nextKb.sampleRequests : [];
            // If both have no ID, compare the entire objects to see if they're actually the same
            // This handles the case where we're creating a new knowledge base
            if (!prevId && !nextId) {
                const isSame = JSON.stringify(prev) === JSON.stringify(nextKb);
                if (isSame) {
                    return prev;
                }
            } else {
                // If they have IDs, compare all relevant properties
                const prevPublished = prev?.published;
                const nextPublished = nextKb?.published;
                const prevOffered = prev?.offered;
                const nextOffered = nextKb?.offered;
                const prevContactEmail = prev?.contactEmail || '';
                const nextContactEmail = nextKb?.contactEmail || '';
                const prevSynopsis = prev?.synopsis || '';
                const nextSynopsis = nextKb?.synopsis || '';
                const prevMarketingContent = prev?.marketingContent || '';
                const nextMarketingContent = nextKb?.marketingContent || '';
                const prevTermsUrl = prev?.urlTermsAndConditions || prev?.termsAndConditionsURL || '';
                const nextTermsUrl = nextKb?.urlTermsAndConditions || nextKb?.termsAndConditionsURL || '';
                const prevPrivacyUrl = prev?.urlPrivacyPolicy || prev?.privacyPolicyURL || '';
                const nextPrivacyUrl = nextKb?.urlPrivacyPolicy || nextKb?.privacyPolicyURL || '';

                // Compare additional editable properties
                const prevName = prev?.name || '';
                const nextName = nextKb?.name || '';
                const prevDescription = prev?.description || '';
                const nextDescription = nextKb?.description || '';
                const prevIcon = prev?.icon || '';
                const nextIcon = nextKb?.icon || '';
                const prevIconColor = prev?.iconColor || '';
                const nextIconColor = nextKb?.iconColor || '';
                const prevTextSimilarity = prev?.textSimilarity;
                const nextTextSimilarity = nextKb?.textSimilarity;
                const prevTextFragmentCount = prev?.textFragmentCount;
                const nextTextFragmentCount = nextKb?.textFragmentCount;
                const prevImageSimilarity = prev?.imageSimilarity;
                const nextImageSimilarity = nextKb?.imageSimilarity;
                const prevImageCount = prev?.imageCount;
                const nextImageCount = nextKb?.imageCount;
                const prevPromptExtension = prev?.promptExtension || '';
                const nextPromptExtension = nextKb?.promptExtension || '';

                const isSame = prevId === nextId &&
                              JSON.stringify(prevLinks) === JSON.stringify(nextLinks) &&
                              JSON.stringify(prevImages) === JSON.stringify(nextImages) &&
                              prevPublished === nextPublished &&
                              prevOffered === nextOffered &&
                              prevContactEmail === nextContactEmail &&
                              prevSynopsis === nextSynopsis &&
                              prevMarketingContent === nextMarketingContent &&
                              prevTermsUrl === nextTermsUrl &&
                              prevPrivacyUrl === nextPrivacyUrl &&
                              JSON.stringify(prevManagedFiles) === JSON.stringify(nextManagedFiles) &&
                              JSON.stringify(prevSampleRequests) === JSON.stringify(nextSampleRequests) &&
                              prevName === nextName &&
                              prevDescription === nextDescription &&
                              prevIcon === nextIcon &&
                              prevIconColor === nextIconColor &&
                              prevTextSimilarity === nextTextSimilarity &&
                              prevTextFragmentCount === nextTextFragmentCount &&
                              prevImageSimilarity === nextImageSimilarity &&
                              prevImageCount === nextImageCount &&
                              prevPromptExtension === nextPromptExtension;
                if (isSame) {
                    return prev;
                }
            }

            // Only create a new KnowledgeBase object if it's a different object or if it's null
            if (!nextKb) {
                return null;
            }
            // If it's the same object reference, return it as-is to avoid re-renders
            if (prev && prev === nextKb) {
                return prev;
            }
            const newKnowledgeBase = new KnowledgeBase(nextKb);
            Object.assign(newKnowledgeBase, nextKb);
            return newKnowledgeBase;
        });
    }, []);

    // Function to update URL when knowledge base is selected
    const updateSelectedKnowledgeBase = useCallback((knowledgeBase: any) => {
        setSelectedKnowledgeBaseSafely(knowledgeBase);

        // Sync local state when selecting a knowledge base
        if (knowledgeBase) {
            setLocalName(knowledgeBase.name || '');
            setLocalDescription(knowledgeBase.description || '');
        } else {
            setLocalName('');
            setLocalDescription('');
        }
        // Selecting another KB invalidates any cached pre-optimization value.
        setPreviousDescription(null);

        const currentParam = searchParams.get('knowledgeBase') || '';
        const newId = knowledgeBase && knowledgeBase._id ? knowledgeBase._id : '';

        // Avoid no-op URL writes
        if (currentParam === newId) {
            return;
        }

        const newSearchParams = new URLSearchParams(searchParams);
        if (newId) {
            newSearchParams.set('knowledgeBase', newId);
            newSearchParams.delete('agentToolset');
        } else {
            // When closing (knowledgeBase is null), clean up all knowledge base-related parameters
            console.log('🔧 KNOWLEDGE BASE - Closing knowledge base, cleaning up URL parameters');
            newSearchParams.delete('knowledgeBase');
            newSearchParams.delete('key'); // Remove key parameter to go back to main view
        }
        setSearchParams(newSearchParams);

        // Reset unsaved changes when selecting a new knowledge base
        setHasUnsavedChanges(false);
    }, [searchParams, setSearchParams, setSelectedKnowledgeBaseSafely]);

    // Wrapper function for updates that should mark as unsaved
    const updateSelectedKnowledgeBaseWithChanges = useCallback((knowledgeBase: any) => {
        setSelectedKnowledgeBaseSafely(knowledgeBase);
        setHasUnsavedChanges(true);
    }, [setSelectedKnowledgeBaseSafely]);

    // Save function for the knowledge base
    const saveKnowledgeBase = async () => {
        if (!selectedKnowledgeBase) return;

        // Ensure local state (name, description) and all other properties are synced before saving
        const knowledgeBaseToSave = {
            ...selectedKnowledgeBase,
            name: localName || selectedKnowledgeBase.name || '',
            description: localDescription || selectedKnowledgeBase.description || '',
            // Explicitly include sampleRequests to ensure it's saved
            sampleRequests: selectedKnowledgeBase.sampleRequests || []
        };

        console.log('Saving knowledge base:', {
            name: knowledgeBaseToSave.name,
            description: knowledgeBaseToSave.description,
            sampleRequests: knowledgeBaseToSave.sampleRequests,
            sampleRequestsCount: knowledgeBaseToSave.sampleRequests?.length || 0
        });

        await userAPI.updateMarketplaceOffer(authentication.customer, knowledgeBaseToSave);
        await loadKnowledgeBases();
        setHasUnsavedChanges(false);
        setPreviousDescription(null);

        // Update the current selected knowledge base with the saved version
        const savedKnowledgeBases = await userAPI.getMarketplaceOffers(authentication.customer);
        const updatedKB = savedKnowledgeBases.find((kb: any) => kb._id === selectedKnowledgeBase._id);
        if (updatedKB) {
            const refreshedKnowledgeBase = new KnowledgeBase(updatedKB);
            Object.assign(refreshedKnowledgeBase, updatedKB);
            setSelectedKnowledgeBase(refreshedKnowledgeBase);
        }
    };

    // Ask the server to rewrite the KB description via the 3-layer pipeline.
    // Sibling KB descriptions stay untouched; only the target's description
    // is changed, in memory only until the user saves.
    const optimizeDescription = useCallback(async () => {
        if (!selectedKnowledgeBase?._id || optimizingDescription) return;
        const prev = localDescription || '';
        const lang = (i18n.resolvedLanguage || 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
        setOptimizingDescription(true);
        try {
            const result = await userAPI.optimizeKnowledgeBaseDescription(
                authentication.customer,
                selectedKnowledgeBase._id,
                lang
            );
            if (result?.description && typeof result.description === 'string') {
                setPreviousDescription(prev);
                setLocalDescription(result.description);
                updateSelectedKnowledgeBaseWithChanges({
                    ...selectedKnowledgeBase,
                    description: result.description,
                });
            }
        } catch (err: any) {
            pushError(err?.message || t('ui.optimizeDescriptionFailed'));
        } finally {
            setOptimizingDescription(false);
        }
    }, [selectedKnowledgeBase, localDescription, authentication?.customer, i18n, pushError, optimizingDescription, updateSelectedKnowledgeBaseWithChanges, t]);

    const revertDescription = useCallback(() => {
        if (previousDescription === null || !selectedKnowledgeBase) return;
        setLocalDescription(previousDescription);
        updateSelectedKnowledgeBaseWithChanges({
            ...selectedKnowledgeBase,
            description: previousDescription,
        });
        setPreviousDescription(null);
    }, [previousDescription, selectedKnowledgeBase, updateSelectedKnowledgeBaseWithChanges]);

    // Function to load knowledge base from URL on page load
    const loadKnowledgeBaseFromURL = async () => {
        const knowledgeBaseId = searchParams.get('knowledgeBase');

        if (knowledgeBaseId && knowledgeBases.length > 0) {
            const knowledgeBase = knowledgeBases.find(kb => kb._id === knowledgeBaseId);
            if (knowledgeBase) {
                updateSelectedKnowledgeBase(knowledgeBase);
            }
        }
    };

    // Link table columns are memoized to avoid re-creating handlers and columns on each render
    const linkColumns: ColumnDef<any>[] = useMemo(() => ([{
        id: 'actions',
        header: plainHeader('Aktionen'),
        size: 160,
        cell: ({ row }) => <div>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('ui.delete')}
                className="text-destructive hover:text-destructive"
                onClick={() => {
                    const link = row.original;
                    const nextLinks = (selectedKnowledgeBase?.links || []).filter((l: any) => l.url !== link.url);
                    updateSelectedKnowledgeBaseWithChanges({ ...selectedKnowledgeBase, links: nextLinks });
                }}
            >
                <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
        </div>
    }, {
        id: 'url',
        header: plainHeader('URL'),
        size: 400,
        cell: ({ row }) => <div className="min-w-0 break-all">{row.original.url}</div>
    }, {
        id: 'depth',
        header: plainHeader('Depth'),
        size: 150,
        cell: ({ row }) => <div>{row.original.depth}</div>
    }]), [selectedKnowledgeBase, updateSelectedKnowledgeBaseWithChanges, t]);

    // Focus link input when dialog opens
    useEffect(() => {
        if (linkDialogOpen && linkInputRef.current) {
            setTimeout(() => linkInputRef.current?.focus(), 0);
        }
    }, [linkDialogOpen]);

    useEffect(() => {
        const keyParam = searchParams.get('key') || 'documents'; // Default to Documents tab
        if (keyParam !== selectedKey) {
            setSelectedKey(keyParam);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!selectedKey) {
            return;
        }
        const currentKey = searchParams.get('key') || 'documents'; // Default to Documents tab
        if (currentKey === selectedKey) {
            return; // avoid no-op writes that can re-trigger effects
        }
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('key', selectedKey);
        setSearchParams(newSearchParams, { replace: true });
    }, [selectedKey]);

    useEffect(() => {
        if (!selectedKnowledgeBase) {
            loadKnowledgeBases();
        }
    }, [selectedKnowledgeBase?._id]); // Only depend on the ID, not the entire object

    useEffect(() => {
        if (knowledgeBases.length > 0) {
            loadKnowledgeBaseFromURL();

            // Validate key when knowledge bases are loaded
            setTimeout(() => {
                validateAndFixSelectedKey();
            }, 100);
        }
    }, [knowledgeBases]);

    const menuItems = withMarketplaceMenuItems([
        {
            key: 'general',
            icon: 'Global',
            name: 'Allgemeine Informationen',
            title: 'Add',
            onClick: () => setSelectedKey('general'),
        },
        {
            key: 'offerStatus',
            icon: 'StatusCircleCheckmark',
            name: 'Angebotsstatus',
            title: 'Angebotsstatus',
            onClick: () => setSelectedKey('offerStatus'),
        },
        {
            key: 'documents',
            icon: 'Documents',
            name: 'Dokumente',
            title: 'Dokumente',
            onClick: () => setSelectedKey('documents'),
        },
        {
            key: 'documentCards',
            icon: 'PageList',
            name: 'Karten',
            title: 'Dokumentkarten',
            onClick: () => setSelectedKey('documentCards'),
        },
        {
            key: 'links',
            icon: 'Link',
            name: 'Links',
            title: 'Links',
            onClick: () => setSelectedKey('links'),
        },
        {
            key: 'parameters',
            icon: 'Settings',
            name: 'Parameter',
            title: 'Parameter',
            onClick: () => setSelectedKey('parameters'),
        },
        {
            key: 'sampleRequests',
            icon: 'SampleRequests',
            name: 'Beispielanfragen',
            title: 'Beispielanfragen',
            onClick: () => setSelectedKey('sampleRequests'),
        },
        {
            key: 'details',
            icon: 'Details',
            name: 'Angebotsdetails',
            title: 'Angebotsdetails',
            onClick: () => setSelectedKey('details'),
        },
        {
            key: 'billing',
            icon: 'Billing',
            name: 'Abrechnung',
            title: 'Abrechnung',
            onClick: () => setSelectedKey('billing'),
        }
    ], authentication?.customerObject);

    // Get valid menu keys for this tool type
    const validMenuKeys = menuItems.map(item => item.key);

    // Validate and fix selectedKey if it's not valid for this tool type
    const validateAndFixSelectedKey = () => {
        if (!validMenuKeys.includes(selectedKey)) {
            console.log('🔧 INVALID KEY - selectedKey not valid for knowledge bases:', selectedKey);
            console.log('🔧 INVALID KEY - valid keys are:', validMenuKeys);
            console.log('🔧 INVALID KEY - falling back to documents');
            setSelectedKey('documents'); // Fallback to Documents tab

            // Update URL to reflect the fallback
            const newParams = new URLSearchParams(searchParams);
            newParams.set('key', 'documents');
            setSearchParams(newParams, { replace: true });
            return true; // Indicates we made a change
        }
        return false; // No change needed
    };

    const onImagesChange = async (imageList: any, _addUpdateIndex: any) => {
        try {
            if (!imageList?.[0]?.file) {
                return;
            }
            // TODO put in API function
            const form = new FormData();
            form.append("file", imageList[0].file);
            const image = await userAPI.uploadFile(form);
            const imageUrl = image?.path || image?.url || image?.fileUrl || image?.logoURL;
            if (!imageUrl) {
                throw new Error('Upload failed: No valid image URL returned from server');
            }
            updateSelectedKnowledgeBaseWithChanges({
                ...selectedKnowledgeBase,
                images: [...(selectedKnowledgeBase.images || []), imageUrl]
            });
        } catch (error: any) {
            pushError(error);
        }
    };

    return <><div className="min-w-0 max-w-[1400px]">
        <style>{monacoStyle}</style>
        {selectedKnowledgeBase
            ?
            <div className="min-w-0 rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-0">
                        <div className="text-sm">Wissensbasis</div>
                        <div className="break-words text-base font-semibold">{selectedKnowledgeBase?.name || ''}</div>
                    </div>
                    <div className="flex flex-1 flex-wrap items-center justify-end gap-4">
                        {hasUnsavedChanges && (
                            <Badge variant="warning" className="gap-1 whitespace-nowrap">
                                <AlertTriangle className="h-3 w-3" aria-hidden />
                                {t('global.unsavedChanges')}
                            </Badge>
                        )}
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        aria-label="Testen"
                                        onClick={() => openTestDialog(selectedKnowledgeBase)}
                                    >
                                        <Rocket className="h-4 w-4" aria-hidden />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Testen</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <Button type="button" variant="outline" onClick={() => {
                            updateSelectedKnowledgeBase(null);
                        }}>{hasUnsavedChanges ? t('ui.cancel') : t('global.close')}</Button>
                        {selectedKnowledgeBase?._id && selectedKnowledgeBase?.lastStatus !== OfferStatus.discontinued ?
                            <Button type="button" onClick={async () => {
                                // Ensure local state (name, description) and all other properties are synced before saving
                                const knowledgeBaseToSave = {
                                    ...selectedKnowledgeBase,
                                    name: localName || selectedKnowledgeBase.name || '',
                                    description: localDescription || selectedKnowledgeBase.description || '',
                                    // Explicitly include sampleRequests to ensure it's saved
                                    sampleRequests: selectedKnowledgeBase.sampleRequests || []
                                };
                                console.log('Saving knowledge base:', {
                                    name: knowledgeBaseToSave.name,
                                    description: knowledgeBaseToSave.description,
                                    sampleRequests: knowledgeBaseToSave.sampleRequests,
                                    sampleRequestsCount: knowledgeBaseToSave.sampleRequests?.length || 0
                                });
                                await userAPI.updateMarketplaceOffer(authentication.customer, knowledgeBaseToSave);
                                await loadKnowledgeBases();
                                updateSelectedKnowledgeBase(null);
                            }}>Speichern</Button>
                            :
                            <Button type="button" onClick={async () => {
                                // Ensure local state (name, description) and all other properties are synced before creating
                                const knowledgeBaseToSave = {
                                    ...selectedKnowledgeBase,
                                    name: localName || selectedKnowledgeBase.name || '',
                                    description: localDescription || selectedKnowledgeBase.description || '',
                                    // Explicitly include sampleRequests to ensure it's saved
                                    sampleRequests: selectedKnowledgeBase.sampleRequests || []
                                };
                                console.log('Creating knowledge base:', {
                                    name: knowledgeBaseToSave.name,
                                    description: knowledgeBaseToSave.description,
                                    sampleRequests: knowledgeBaseToSave.sampleRequests,
                                    sampleRequestsCount: knowledgeBaseToSave.sampleRequests?.length || 0
                                });
                                await userAPI.createMarketplaceOffer(authentication.customer, knowledgeBaseToSave);
                                await loadKnowledgeBases();
                                updateSelectedKnowledgeBase(null);
                            }}>Erstellen</Button>
                        }
                    </div>
                </div>
                <div className="mt-3 flex min-w-0 flex-wrap gap-4 rounded-md border p-3">
                    <div className="w-[200px] shrink-0">
                        {menuItems.map((item) => (
                            <button
                                type="button"
                                key={item.key}
                                className={cn(
                                    'flex w-full items-center rounded-md p-3 text-left text-sm hover:bg-accent',
                                    selectedKey === item.key && 'bg-muted'
                                )}
                                onClick={() => setSelectedKey(item.key)}
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>
                    {/* A flex item defaults to min-width:auto and refuses to shrink below its
                        content, so a wide child would stretch this column past the card
                        instead of scrolling inside it. */}
                    <div className="min-w-0 flex-1">
                        {(() => {
                            switch (selectedKey) {
                                case 'general':
                                    return (
                                        <div className="w-full">
                                            <div className="flex flex-wrap gap-6">
                                                <div className="min-w-0 flex-1 basis-[300px]">
                                                    <div className="flex flex-col gap-1.5">
                                                        <Label htmlFor={`${fieldId}-name`}>Name der Wissensbasis</Label>
                                                        <Input
                                                            id={`${fieldId}-name`}
                                                            value={localName}
                                                            onChange={(event) => {
                                                                setLocalName(event.target.value || '');
                                                            }}
                                                            onBlur={() => {
                                                                // Update the main state when user finishes editing
                                                                if (selectedKnowledgeBase && localName !== selectedKnowledgeBase.name) {
                                                                    updateSelectedKnowledgeBaseWithChanges({
                                                                        ...selectedKnowledgeBase,
                                                                        name: localName
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3 flex flex-col gap-1.5">
                                                        <Label htmlFor={`${fieldId}-description`}>Beschreibung der Wissensbasis</Label>
                                                        <Textarea
                                                            id={`${fieldId}-description`}
                                                            rows={15}
                                                            value={localDescription}
                                                            onChange={(event) => {
                                                                setLocalDescription(event.target.value || '');
                                                            }}
                                                            onBlur={() => {
                                                                // Update the main state when user finishes editing
                                                                if (selectedKnowledgeBase && localDescription !== selectedKnowledgeBase.description) {
                                                                    updateSelectedKnowledgeBaseWithChanges({
                                                                        ...selectedKnowledgeBase,
                                                                        description: localDescription
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3 text-sm text-primary">Diese Kurzbeschreibung wird von der AI verwendet, um deine Wissensbasis weiter zu durchsuchen, wenn sie zur Frage eines Nutzers passt.</div>
                                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                                        {optimizingDescription ? (
                                                            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                                                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                                                                {t('ui.optimizingDescription')}
                                                            </span>
                                                        ) : previousDescription !== null ? (
                                                            <button
                                                                type="button"
                                                                className="text-sm text-primary underline-offset-4 hover:underline"
                                                                onClick={revertDescription}
                                                            >
                                                                {t('ui.revertDescription')}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="text-sm text-primary underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                                                                onClick={optimizeDescription}
                                                                disabled={!selectedKnowledgeBase?._id}
                                                            >
                                                                {t('ui.optimizeDescriptionForAI')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="min-w-0 flex-1 basis-[300px]">
                                                    <div className="flex min-w-0 flex-wrap gap-4">
                                                        <FluentIconSelector
                                                            label="Icon"
                                                            selectedIcon={selectedKnowledgeBase?.icon || 'CityNext'}
                                                            iconColor={selectedKnowledgeBase?.iconColor}
                                                            onIconChange={(iconName) => {
                                                                updateSelectedKnowledgeBaseWithChanges({ ...selectedKnowledgeBase, icon: iconName });
                                                            }}
                                                        />
                                                        <ColorSelector
                                                            label="Icon Color"
                                                            selectedColor={selectedKnowledgeBase?.iconColor}
                                                            onColorChange={(color) => {
                                                                updateSelectedKnowledgeBaseWithChanges({ ...selectedKnowledgeBase, iconColor: color });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <InternallyAvailableCheckbox
                                                            marketplaceOffer={selectedKnowledgeBase}
                                                            updateSelectedMarketplaceOffer={updateSelectedKnowledgeBaseWithChanges}
                                                        />
                                                    </div>

                                                    {selectedKnowledgeBase._id && (
                                                        <div className="mt-3">
                                                            <ToolUsageList
                                                                toolId={selectedKnowledgeBase._id}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={saveKnowledgeBase}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                case 'details':
                                    return (
                                        <MarketingDetails
                                            type={KnowledgeBase.type}
                                            marketplaceOffer={selectedKnowledgeBase as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedKnowledgeBaseWithChanges}
                                            onImagesChange={onImagesChange}
                                        />
                                    );
                                case 'offerStatus':
                                    return (
                                        <OfferStatusManagement
                                            marketplaceOffer={selectedKnowledgeBase}
                                            updateSelectedMarketplaceOffer={updateSelectedKnowledgeBaseWithChanges}
                                        />
                                    );
                                case 'documents':
                                    return (
                                        <FileManagement
                                            selectedKnowledgeBase={selectedKnowledgeBase}
                                            updateSelectedKnowledgeBase={updateSelectedKnowledgeBaseWithChanges}
                                            authentication={authentication}
                                            pushError={pushError}
                                            showDeleteDialog={showDeleteDialog}
                                        />
                                    );
                                case 'documentCards':
                                    return (
                                        <DocumentCardsPanel
                                            selectedKnowledgeBase={selectedKnowledgeBase}
                                            authentication={authentication}
                                            pushError={pushError}
                                            showDeleteDialog={showDeleteDialog}
                                        />
                                    );
                                case 'links':
                                    return (
                                        <>
                                            <div className="mt-5 text-sm">Wähle relevante Links aus, die in diesem Wissensangebot verwendet werden sollen.</div>
                                            <Button type="button" variant="ghost" className="mt-5 gap-2" onClick={() => {
                                                if (!linkDialogOpen) {
                                                    setLinkUrl('');
                                                    setLinkDialogOpen(true);
                                                    // focus will be handled by effect below
                                                }
                                            }}>
                                                <Plus className="h-4 w-4" aria-hidden />
                                                Link hinzufügen
                                            </Button>
                                            <div className="mt-5 overflow-x-auto">
                                                <DataTable
                                                    data={selectedKnowledgeBase.links || []}
                                                    columns={linkColumns}
                                                    page={kbLinksPagination.page}
                                                    onPageChange={kbLinksPagination.onPageChange}
                                                    emptyMessage={t('settingsPanel.dataTable.noRecordsText')}
                                                />
                                            </div>
                                        </>
                                    );
                                case 'parameters':
                                    return (
                                        <div>
                                            <div className="mt-5 text-sm">Konfiguriere die Parameter für die RAG-Suche in dieser Wissensbasis.</div>
                                            <div className="mt-5 flex flex-wrap gap-6">
                                                <div className="w-[200px] shrink-0">
                                                    <div className="mb-4 text-sm font-bold">Textfragmente</div>
                                                    <div className="mt-5 flex flex-col gap-1.5">
                                                        <Label htmlFor={`${fieldId}-text-similarity`}>Themenübereinstimmung</Label>
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                id={`${fieldId}-text-similarity`}
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                value={selectedKnowledgeBase.textSimilarity ? Math.round(selectedKnowledgeBase.textSimilarity * 100).toString() : '95'}
                                                                onChange={(event) => {
                                                                    const value = event.target.value;
                                                                    const numValue = value ? parseInt(value) : 95;
                                                                    updateSelectedKnowledgeBaseWithChanges({
                                                                        ...selectedKnowledgeBase,
                                                                        textSimilarity: numValue / 100
                                                                    });
                                                                }}
                                                            />
                                                            <span className="text-sm text-muted-foreground">%</span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-5 flex flex-col gap-1.5">
                                                        <Label htmlFor={`${fieldId}-text-fragments`}>Anzahl Textfragmente</Label>
                                                        <Input
                                                            id={`${fieldId}-text-fragments`}
                                                            type="number"
                                                            min={1}
                                                            max={50}
                                                            value={selectedKnowledgeBase.textFragmentCount?.toString() || '10'}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                const numValue = value ? parseInt(value) : 10;
                                                                updateSelectedKnowledgeBaseWithChanges({
                                                                    ...selectedKnowledgeBase,
                                                                    textFragmentCount: numValue
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="w-[200px] shrink-0">
                                                    <div className="mb-4 text-sm font-bold">Bilder</div>
                                                    <div className="mt-5 flex flex-col gap-1.5">
                                                        <Label htmlFor={`${fieldId}-image-similarity`}>Themenübereinstimmung</Label>
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                id={`${fieldId}-image-similarity`}
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                value={selectedKnowledgeBase.imageSimilarity ? Math.round(selectedKnowledgeBase.imageSimilarity * 100).toString() : '80'}
                                                                onChange={(event) => {
                                                                    const value = event.target.value;
                                                                    const numValue = value ? parseInt(value) : 80;
                                                                    updateSelectedKnowledgeBaseWithChanges({
                                                                        ...selectedKnowledgeBase,
                                                                        imageSimilarity: numValue / 100
                                                                    });
                                                                }}
                                                            />
                                                            <span className="text-sm text-muted-foreground">%</span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-5 flex flex-col gap-1.5">
                                                        <Label htmlFor={`${fieldId}-image-count`}>Anzahl Bilder</Label>
                                                        <Input
                                                            id={`${fieldId}-image-count`}
                                                            type="number"
                                                            min={1}
                                                            max={20}
                                                            value={selectedKnowledgeBase.imageCount?.toString() || '5'}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                const numValue = value ? parseInt(value) : 5;
                                                                updateSelectedKnowledgeBaseWithChanges({
                                                                    ...selectedKnowledgeBase,
                                                                    imageCount: numValue
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="min-w-0 flex-1 basis-[300px]">
                                                    <div className="mb-4 text-sm font-bold">Prompterweiterung</div>
                                                    <div className="mt-5">
                                                        <Textarea
                                                            aria-label="Prompterweiterung"
                                                            rows={10}
                                                            value={selectedKnowledgeBase.promptExtension || ''}
                                                            onChange={(event) => {
                                                                updateSelectedKnowledgeBaseWithChanges({
                                                                    ...selectedKnowledgeBase,
                                                                    promptExtension: event.target.value || ''
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3 text-sm text-primary">
                                                        Dieses Prompt wird zu den Textbausteinen der Wissensbasis ergänzt und kann weiter verfeinern, wie die gefundenen Textbausteine ausgewertet werden.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                case 'sampleRequests':
                                    return (
                                        <SampleRequests
                                            type={KnowledgeBase.type}
                                            marketplaceOffer={selectedKnowledgeBase as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedKnowledgeBaseWithChanges}
                                        />
                                    );
                                case 'billing':
                                    return (
                                        <PlanPanel
                                            type={KnowledgeBase.type}
                                            marketplaceOffer={selectedKnowledgeBase as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedKnowledgeBaseWithChanges}
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </div>
                </div>
            </div>
            :
            <div>
                <div className="flex flex-wrap items-center gap-3">
                    <NameFilterBar
                        wert={knowledgeBaseFilter}
                        onChange={setKnowledgeBaseFilter}
                        seite={knowledgeBasePagination.page}
                        onSeiteChange={knowledgeBasePagination.onPageChange}
                        anzahl={gefilterteKnowledgeBases.length}
                        gesamt={(knowledgeBases ?? []).length}
                    />
                    <Button data-guide-id="settings.agent.knowledgeBase.create" type="button" variant="ghost" className="gap-2" onClick={async () => {
                        const newKnowledgeBase = new KnowledgeBase({
                            name: 'Neue Wissensbasis',
                            synopsis: '',
                            description: '',
                            marketingContent: '',
                            urlTermsAndConditions: '',
                            urlPrivacyPolicy: '',
                            files: [],
                            getTypeClass: 'KnowledgeBase',
                            getTypeName: 'KnowledgeBase'
                        });
                        updateSelectedKnowledgeBase(newKnowledgeBase);
                    }}>
                        <Plus className="h-4 w-4" aria-hidden />
                        Wissensbasis hinzufügen
                    </Button>
                </div>
                <div className="mt-5 overflow-x-auto">
                    <DataTable
                        data={gefilterteKnowledgeBases}
                        columns={knowledgeBaseColumns}
                        page={knowledgeBasePagination.page}
                        onPageChange={knowledgeBasePagination.onPageChange}
                        loading={loadingKnowledgeBases && knowledgeBases.length === 0}
                        onRowClick={updateSelectedKnowledgeBase}
                        emptyMessage={<div className="p-6">Keine Wissensbasen gefunden</div>}
                    />
                </div>
            </div>
        }
    </div >
        {TestDialog}
        <Dialog
            open={linkDialogOpen}
            onOpenChange={(open) => {
                if (!open) setLinkDialogOpen(false);
            }}
        >
            <DialogContent className="w-[min(1000px,95vw)] max-w-none">
                <DialogHeader>
                    <DialogTitle>Link hinzufügen</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`${fieldId}-link-url`}>URL</Label>
                    <Input
                        id={`${fieldId}-link-url`}
                        ref={linkInputRef}
                        value={linkUrl}
                        onChange={(event) => {
                            setLinkUrl(event.target.value || '');
                        }}
                    />
                </div>
                <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => {
                        if (linkDialogOpen) {
                            setLinkDialogOpen(false);
                        }
                    }}>{t('global.close')}</Button>
                    <Button type="button" disabled={!linkUrl || linkUrl.trim().length === 0} onClick={() => {
                        if (!linkUrl || linkUrl.trim().length === 0) {
                            return;
                        }
                        const nextLinks = [...(selectedKnowledgeBase?.links || []), { url: linkUrl, depth: 1 }];
                        updateSelectedKnowledgeBaseWithChanges({ ...selectedKnowledgeBase, links: nextLinks });
                        if (linkDialogOpen) {
                            setLinkDialogOpen(false);
                        }
                    }}>{t('global.create')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </>;
}
